import {
  CommunityTitle,
  Divider,
  Loader,
  MenuChatList,
  MenuItem,
  MenuList,
  MenuMembersList,
  PendingSettingsInvite,
  SettingsButton,
  Tooltip,
} from 'components';
import { COMMUNITY_ROUTES, ROUTES } from 'configs';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useToasts } from 'react-toast-notifications';
import { useLocalStorage } from 'react-use';
import {
  ChatIcon,
  DocumentationIcon,
  GroupIcon,
  HomePageIcon,
  MailIcon,
  PushNotificationLogo,
} from 'static';
import { doToggleMenu } from 'store/actionCreators/currentCommunity';
import { openModal } from 'store/actionCreators/modal';
import { getMessageUnSeenCount } from 'store/selectors/auth';
import { getChatUser, getChatUserLoading } from 'store/selectors/chat';
import {
  getCommunities,
  getCommunitiesLoading,
} from 'store/selectors/communities';
import {
  getCurrentCommunity,
  getCurrentUserInCommunity,
  getIsMenuOpen,
} from 'store/selectors/currentCommunity';
import { CommunityRoles, ModalTypes } from 'types';
import { commonSetStorageCommunity } from 'utils/commonStorage';

import {
  StyledBurger,
  StyledMenuBlock,
  StyledMenuHeader,
  StyledMenuItemsBlock,
  StyledSettingsLink,
  StyledTooltipTriggerWrap,
} from './styled';

const CommunityMenu = () => {
  const HEIGHT_RESERVE = 324;
  const RESIZE_STEP = 5;
  const MIN_VISIBLE_LIST_HEIGHT = 120;
  const MIN_VISIBLE_CHAT_HEIGHT = 90;

  const { t } = useTranslation();
  const { communityId } = useParams();
  const { addToast } = useToasts();
  const dispatch = useDispatch();
  const community = useSelector(getCurrentCommunity);
  const communities = useSelector(getCommunities);
  const currentUser = useSelector(getCurrentUserInCommunity);
  const isMenuOpen = useSelector(getIsMenuOpen);
  const isCommunitiesLoading = useSelector(getCommunitiesLoading);
  const chatUser = useSelector(getChatUser);
  const chatUserLoading = useSelector(getChatUserLoading);
  const messageUnSeenCount = useSelector(getMessageUnSeenCount);
  const groups = community.groups || community.overallScheduler?.groups;
  const userRole = currentUser?.communityPermission.role;
  const { users = [] } = community;
  const [listMaxHeights, setListMaxHeights] = useState({
    groups: 0,
    members: 0,
    chats: 0,
  });
  const [menuState, setMenuState] = useLocalStorage<string>('menuShown', '');
  const navigate = useNavigate();
  const isLoading = isCommunitiesLoading;
  const invitesCount = communities.find((c) => c.id === community.id)?.requests
    ?.length;

  // Refs
  const groupsListRef = useRef(null);
  const membersListRef = useRef(null);
  const chatsListRef = useRef(null);
  const uniqueUsersRef = useRef([]);
  const previousChatUserRef = useRef([]);
  if (chatUser && chatUser.length > 0) {
    const newUniqueUsers = chatUser.slice(0, 3);
    const isRemoved =
      previousChatUserRef.current.length > newUniqueUsers.length;
    if (
      isRemoved ||
      JSON.stringify(previousChatUserRef.current) !==
        JSON.stringify(newUniqueUsers)
    ) {
      uniqueUsersRef.current = newUniqueUsers;
      previousChatUserRef.current = newUniqueUsers;
    }
  } else {
    uniqueUsersRef.current = [];
    previousChatUserRef.current = [];
  }

  const handleGroupEventPopupOpen = (data: string) => {
    if (data !== 'chat') {
      commonSetStorageCommunity(`${ROUTES.COMMUNITY}/${communityId}/overall`);
      navigate(`${ROUTES.COMMUNITY}/${communityId}/overall`);
    }

    setTimeout(() => {
      dispatch(
        openModal({
          type:
            data === 'chat'
              ? ModalTypes.CHAT_POPUP
              : ModalTypes.COMMUNITY_POPUP,
        }),
      );
    }, 1500);
  };

  useEffect(() => {
    if (!menuState) {
      setMenuState('true');
      dispatch(doToggleMenu());
    }

    window.addEventListener('resize', setListsHeight);

    return () => window.removeEventListener('resize', setListsHeight);
  }, []);

  useEffect(() => {
    setListsHeight();
  }, [community]);

  const setListsHeight = () => {
    let groupsListHeight =
      groupsListRef?.current?.getBoundingClientRect().height + 10 || 0;
    let chatsListHeight =
      chatsListRef?.current?.getBoundingClientRect().height || 0;
    let membersListHeight =
      membersListRef?.current?.getBoundingClientRect().height + 10 || 0;
    const heightLeft = Math.max(window.innerHeight - HEIGHT_RESERVE, 0);

    const hasGroups = groupsListHeight > 0;
    const hasChats = chatsListHeight > 0;
    const hasMembers = membersListHeight > 0;

    const minGroupsHeight = hasGroups ? MIN_VISIBLE_LIST_HEIGHT : 0;
    const minChatsHeight = hasChats ? MIN_VISIBLE_CHAT_HEIGHT : 0;
    const minMembersHeight = hasMembers ? MIN_VISIBLE_LIST_HEIGHT : 0;

    const clampToMin = (
      currentHeight: number,
      minHeight: number,
      shouldClamp: boolean,
    ) => (shouldClamp ? Math.max(currentHeight, minHeight) : currentHeight);

    groupsListHeight = clampToMin(groupsListHeight, minGroupsHeight, hasGroups);
    chatsListHeight = clampToMin(chatsListHeight, minChatsHeight, hasChats);
    membersListHeight = clampToMin(
      membersListHeight,
      minMembersHeight,
      hasMembers,
    );

    const reduceHeight = (
      currentHeight: number,
      minHeight: number,
      overflowLeft: number,
      shouldReduce: boolean,
    ) => {
      if (!shouldReduce || overflowLeft <= 0 || currentHeight <= minHeight) {
        return { height: currentHeight, overflow: overflowLeft };
      }

      const reducible = currentHeight - minHeight;
      const reducedBy = Math.min(reducible, overflowLeft);

      return {
        height: currentHeight - reducedBy,
        overflow: overflowLeft - reducedBy,
      };
    };

    const totalHeight = groupsListHeight + chatsListHeight + membersListHeight;
    let overflow = totalHeight - heightLeft;

    if (overflow > 0) {
      const groupsReduced = reduceHeight(
        groupsListHeight,
        minGroupsHeight,
        overflow,
        hasGroups,
      );
      groupsListHeight = groupsReduced.height;
      overflow = groupsReduced.overflow;
    }

    if (overflow > 0) {
      const membersReduced = reduceHeight(
        membersListHeight,
        minMembersHeight,
        overflow,
        hasMembers,
      );
      membersListHeight = membersReduced.height;
      overflow = membersReduced.overflow;
    }

    if (overflow > 0) {
      const chatsReduced = reduceHeight(
        chatsListHeight,
        minChatsHeight,
        overflow,
        hasChats,
      );
      chatsListHeight = chatsReduced.height;
      overflow = chatsReduced.overflow;
    }

    // On very small viewports, keep shrinking all sections until they fit.
    while (overflow > 0) {
      if (groupsListHeight > 0) {
        const delta = Math.min(groupsListHeight, RESIZE_STEP, overflow);
        groupsListHeight -= delta;
        overflow -= delta;
      }

      if (overflow > 0 && membersListHeight > 0) {
        const delta = Math.min(membersListHeight, RESIZE_STEP, overflow);
        membersListHeight -= delta;
        overflow -= delta;
      }

      if (overflow > 0 && chatsListHeight > 0) {
        const delta = Math.min(chatsListHeight, RESIZE_STEP, overflow);
        chatsListHeight -= delta;
        overflow -= delta;
      }

      if (
        groupsListHeight <= 0 &&
        membersListHeight <= 0 &&
        chatsListHeight <= 0
      ) {
        break;
      }
    }

    setListMaxHeights({
      groups: groupsListHeight,
      chats: chatsListHeight,
      members: membersListHeight,
    });
  };

  const handleMenuToggle = useCallback(() => {
    setMenuState(menuState ? 'true' : '');
    dispatch(doToggleMenu());
  }, [menuState, dispatch]);

  const commonMemberProps = useMemo(() => {
    const filteredUsers = users
      .filter((u) => u.communityPermission?.role !== CommunityRoles.BLOCKED)
      .map((li) => ({
        ...li,
        unreadCount:
          chatUser?.find((u) => u.peerUsers?.userId === li.id)
            ?.chatUnreadCount || 0,
      }));

    return {
      list: [],
      listRef: membersListRef,
      listMaxHeight: listMaxHeights.members,
      userRole,
      users: filteredUsers,
      groupList: groups,
    };
  }, [
    users,
    chatUser,
    membersListRef,
    listMaxHeights.members,
    userRole,
    groups,
  ]);

  const handleOnArrowClick = () => {
    commonSetStorageCommunity(
      `${ROUTES.COMMUNITY}/${community?.id}/overall/#members`,
    );
    navigate(`${ROUTES.COMMUNITY}/${community?.id}/overall/#members`);
  };

  const tooltipProps = {
    tooltipStyle: {
      fontSize: 12,
      maxWidth: 240,
      whiteSpace: 'pre-wrap',
      lineBreak: 'normal',
    },
  };

  return (
    <StyledMenuBlock isMenuOpen={isMenuOpen}>
      <StyledMenuHeader>
        {invitesCount > 0 &&
          [CommunityRoles.ADMIN, CommunityRoles.OWNER].includes(userRole) && (
            <PendingSettingsInvite
              invitesCount={invitesCount}
              addCss={{
                right: isMenuOpen ? 34 : 4,
                top: 6,
              }}
            />
          )}
        <CommunityTitle
          title={community ? community.name : ''}
          image={community ? community.logo : ''}
          isMenuOpen={isMenuOpen}
          frame
        />
        {isMenuOpen &&
        [CommunityRoles.ADMIN, CommunityRoles.OWNER].includes(userRole) ? (
          <Tooltip
            {...tooltipProps}
            isVisible
            text={t('community_settings.tooltip')}
            position="bottom-center"
          >
            <StyledTooltipTriggerWrap>
              <StyledSettingsLink
                end
                to={`${ROUTES.COMMUNITY}/${community && community.id}/${
                  COMMUNITY_ROUTES.SETTINGS
                }`}
              >
                <SettingsButton />
              </StyledSettingsLink>
            </StyledTooltipTriggerWrap>
          </Tooltip>
        ) : null}
        <StyledBurger isMenuOpen={isMenuOpen} onClick={handleMenuToggle}>
          <div></div>
          <div></div>
          <div></div>
        </StyledBurger>
      </StyledMenuHeader>
      <Divider customMargin="0px 0px 16px 0px" />
      {community && !isLoading ? (
        <StyledMenuItemsBlock isMenuOpen={isMenuOpen}>
          <MenuItem
            title={t('menu.homePage')}
            icon={<HomePageIcon />}
            to={`${ROUTES.COMMUNITY}/${community.id}`}
          />
          <MenuList
            title={t('menu.groupScheduler')}
            titleLink={`${ROUTES.COMMUNITY}/${community.id}/${COMMUNITY_ROUTES.OVERALL}`}
            icon={<GroupIcon />}
            list={community.groups}
            isAddButtonContained
            userRole={userRole}
            listRef={groupsListRef}
            listMaxHeight={listMaxHeights.groups}
            onClick={handleGroupEventPopupOpen}
            communityId={community.id}
          />
          <MenuItem
            to={`${ROUTES.COMMUNITY}/${community.id}/${COMMUNITY_ROUTES.DOCUMENTATION}`}
            title={t('menu.documentation')}
            icon={<DocumentationIcon />}
          />
          <MenuItem
            to={`${ROUTES.COMMUNITY}/${community.id}/${COMMUNITY_ROUTES.PUSH_NOTIFICATION}`}
            title={t('pushNotification.header')}
            isAddButtonContained
            icon={<PushNotificationLogo />}
            toolTipText={t('pushNotification.plusTooltip')}
            isAdminOrOwner={[
              CommunityRoles.ADMIN,
              CommunityRoles.OWNER,
            ].includes(userRole)}
            onClick={() => {
              addToast(t('pushNotification.toasterText'), {
                appearance: 'success',
                autoDismiss: false,
              });
            }}
          />
          <MenuItem
            to={`${ROUTES.COMMUNITY}/${community.id}/${COMMUNITY_ROUTES.EMAIL}`}
            title={t('common.email')}
            icon={<MailIcon />}
            toolTipText={t('email.plusTooltip')}
            isAdminOrOwner={[
              CommunityRoles.ADMIN,
              CommunityRoles.OWNER,
            ].includes(userRole)}
            isAddButtonContained
            onClick={() => {}}
          />
          <MenuItem
            to={`${ROUTES.COMMUNITY}/${community.id}/${COMMUNITY_ROUTES.CHAT}`}
            title={t('menu.chats')}
            icon={<ChatIcon />}
            isAddButtonContained
            toolTipText={t('chat.plusTooltip')}
            onClick={handleGroupEventPopupOpen}
            count={messageUnSeenCount}
          />
          <MenuChatList
            commonMember={uniqueUsersRef.current}
            chatUserLoading={chatUserLoading}
            communityId={community.id}
            listMaxHeight={listMaxHeights.chats}
            listRef={chatsListRef}
            onClick={handleGroupEventPopupOpen}
          />
          <MenuMembersList
            handleOnArrowClick={handleOnArrowClick}
            showArrow
            to={`${ROUTES.COMMUNITY}/${community?.id}/overall/#members`}
            {...commonMemberProps}
          />
        </StyledMenuItemsBlock>
      ) : (
        isMenuOpen && <Loader />
      )}
    </StyledMenuBlock>
  );
};

export default CommunityMenu;
